import { type PermissionGrant, type RoleProfile } from './types';

export interface CanScope {
  warehouseId?: string | null;
  partyId?: string | null;
  partyIds?: string[];
  organizationId?: string | null;
}

/**
 * Matches a permission code against a grant code — the same rules as the backend's `PermissionMatcher`:
 * `*` matches everything, a trailing `ctx:*` / `ctx:res:*` matches everything below it, and a `*` segment in the
 * middle matches exactly one segment (`party:*:read` covers `party:party:read`, not `party:party:manage`).
 * The web used to understand only the trailing form, so every `ctx:*:read` a role holds was invisible to the
 * navigation and the actions (W2).
 */
export function permissionMatches(grantCode: string, permission: string): boolean {
  if (grantCode === '*' || grantCode === permission) return true;
  const g = grantCode.split(':');
  const r = permission.split(':');
  for (let i = 0; i < g.length; i++) {
    const last = i === g.length - 1;
    if (g[i] === '*' && last) return r.length >= i;
    if (i >= r.length) return false;
    if (g[i] !== '*' && g[i] !== r[i]) return false;
  }
  return g.length === r.length;
}

function inSet(set: string[] | 'ALL' | undefined, id: string): boolean {
  if (set === undefined || set === 'ALL') return true;
  return set.includes(id);
}

function scopeCovers(grant: PermissionGrant, scope: CanScope | undefined): boolean {
  if (!scope) return true;
  if (scope.warehouseId && !inSet(grant.warehouseIds, scope.warehouseId)) return false;
  const parties: string[] = [];
  if (scope.partyId) parties.push(scope.partyId);
  if (scope.partyIds) parties.push(...scope.partyIds);
  for (const partyId of parties) {
    if (!inSet(grant.partyIds, partyId)) return false;
  }
  if (scope.organizationId && !inSet(grant.organizationIds, scope.organizationId)) return false;
  return true;
}

/**
 * Deny-by-default permission check mirroring `AuthorizationService.check` (15-authorization-model.md):
 * a grant applies when its code matches and its scope covers the resource context — warehouse in set
 * or ALL, every party id of the resource in the allowed set or ALL, organization in set or ALL.
 */
export function can(grants: PermissionGrant[], permission: string, scope?: CanScope): boolean {
  if (!permission) return false;
  for (const grant of grants) {
    if (permissionMatches(grant.code, permission) && scopeCovers(grant, scope)) return true;
  }
  return false;
}

/** All permissions required. */
export function canAll(grants: PermissionGrant[], permissions: string[], scope?: CanScope): boolean {
  return permissions.length > 0 && permissions.every((p) => can(grants, p, scope));
}

/** Any permission suffices. */
export function canAny(grants: PermissionGrant[], permissions: string[], scope?: CanScope): boolean {
  return permissions.some((p) => can(grants, p, scope));
}

const PROFILE_PRECEDENCE: RoleProfile[] = ['admin', 'executive', 'manager', 'supervisor', 'analyst', 'customer', 'carrier', 'driver', 'operator'];

const ROLE_CODE_PROFILES: Record<string, RoleProfile> = {
  TENANT_ADMIN: 'admin',
  PLATFORM_OPERATOR: 'admin',
  INTEGRATION_ADMIN: 'admin',
  EXECUTIVE: 'executive',
  ANALYST: 'analyst',
  WAREHOUSE_MANAGER: 'manager',
  INVENTORY_CONTROLLER: 'manager',
  TRANSPORT_PLANNER: 'manager',
  BILLING_CLERK: 'manager',
  SUPERVISOR: 'supervisor',
  YARD_COORDINATOR: 'supervisor',
  LOGISTICS_CUSTOMER_USER: 'customer',
  CUSTOMER_ADMIN: 'customer',
  // P-32 (D-132): the carrier portal's roles land in their own shell.
  CARRIER_USER: 'carrier',
  CARRIER_ADMIN: 'carrier',
  // P-33 (D-135): the driver works in the driver app; the web only tells it so.
  DRIVER: 'driver',
};

/** Maps a platform role code to its default UI profile. */
export function roleCodeProfile(code: string): RoleProfile {
  const upper = code.toUpperCase();
  if (upper.startsWith('STUDIO_')) return 'supervisor';
  return ROLE_CODE_PROFILES[upper] ?? 'operator';
}

/**
 * Picks the UI profile for a set of roles: an explicit profile wins, otherwise each role's
 * `uiProfile` (or the platform mapping of its code) is taken and the highest-precedence profile
 * (admin > executive > manager > supervisor > analyst > customer > carrier > driver > operator) is returned.
 */
export function deriveRoleProfile(roles: Array<{ code: string; uiProfile?: RoleProfile }>, explicit?: RoleProfile): RoleProfile {
  if (explicit) return explicit;
  const profiles = new Set<RoleProfile>();
  for (const role of roles) profiles.add(role.uiProfile ?? roleCodeProfile(role.code));
  for (const candidate of PROFILE_PRECEDENCE) {
    if (profiles.has(candidate)) return candidate;
  }
  return 'operator';
}
